"use client";

import type { Order, OrderItem } from "@/types/prisma";
import {
  ChevronDown,
  ChevronUp,
  CreditCard,
  ExternalLink,
  Eye,
  MapPin,
  Maximize2,
  Package,
  PackageCheck,
  Phone,
  Search,
  Truck,
  X,
  XCircle,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { AdminPagination } from "@/components/admin/admin-pagination";
import { formatPrice } from "@/lib/utils";
import { ItemVariantBadge, resolveItemVariant } from "@/components/common/item-variant-badge";

/** `Prisma.OrderGetPayload<{ include: { items: { include: { product } }, user } }>`, written out. */
type AdminOrder = Order & {
  items: OrderItem[];
  user: { name: string | null; email: string } | null;
};

const ALL_STATUSES: AdminOrder["status"][] = [
  "PENDING",
  "CONFIRMED",
  "PAID",
  "PROCESSING",
  "PACKED",
  "SHIPPED",
  "OUT_FOR_DELIVERY",
  "DELIVERED",
  "CANCELLED",
  "RETURNED",
];

function statusTone(status: AdminOrder["status"]): "success" | "deal" | "prime" | "default" {
  if (status === "DELIVERED") return "success";
  if (status === "CANCELLED" || status === "RETURNED") return "deal";
  return "prime";
}

function getCategoryBadge(category?: string | null) {
  const cat = (category || "").toLowerCase();
  if (cat.includes("phone") || cat.includes("mobile")) {
    return {
      label: "📱 Mobiles & Accessories",
      color: "bg-sky-100 text-sky-800 border-sky-200 dark:bg-sky-950/60 dark:text-sky-300 dark:border-sky-800/40",
    };
  }
  if (cat.includes("laptop") || cat.includes("computer") || cat.includes("pc")) {
    return {
      label: "💻 Laptops & PC",
      color: "bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-950/60 dark:text-purple-300 dark:border-purple-800/40",
    };
  }
  if (cat.includes("audio") || cat.includes("airpod") || cat.includes("earbud") || cat.includes("headphone")) {
    return {
      label: "🎧 Audio & Earbuds",
      color: "bg-teal-100 text-teal-800 border-teal-200 dark:bg-teal-950/60 dark:text-teal-300 dark:border-teal-800/40",
    };
  }
  if (cat.includes("watch") || cat.includes("smart")) {
    return {
      label: "⌚ Wearables",
      color: "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950/60 dark:text-amber-300 dark:border-amber-800/40",
    };
  }
  if (cat.includes("electronic")) {
    return {
      label: "⚡ Electronics",
      color: "bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-950/60 dark:text-indigo-300 dark:border-indigo-800/40",
    };
  }
  return {
    label: category || "General Merchandise",
    color: "bg-slate-100 text-slate-800 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-white/10",
  };
}

export function OrderManager({ initialOrders }: { initialOrders: AdminOrder[] }) {
  const [orders, setOrders] = useState(initialOrders);
  useEffect(() => setOrders(initialOrders), [initialOrders]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<AdminOrder["status"] | "ALL">("ALL");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detailModalOrder, setDetailModalOrder] = useState<AdminOrder | null>(null);
  const [enlargedImage, setEnlargedImage] = useState<{ url: string; title: string } | null>(null);

  const activeModalOrder = useMemo(() => {
    if (!detailModalOrder) return null;
    return orders.find((o) => o.id === detailModalOrder.id) || detailModalOrder;
  }, [orders, detailModalOrder]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return orders.filter((o) => {
      const matchesSearch =
        !q ||
        o.id.toLowerCase().includes(q) ||
        (o.user?.name ?? "").toLowerCase().includes(q) ||
        (o.user?.email ?? "").toLowerCase().includes(q) ||
        o.shippingPhone.includes(q) ||
        o.shippingFullName.toLowerCase().includes(q) ||
        o.items.some((i) => {
          const vInfo = resolveItemVariant(i);
          return (
            i.title.toLowerCase().includes(q) ||
            (i.variantTitle ?? "").toLowerCase().includes(q) ||
            (vInfo.color ?? "").toLowerCase().includes(q) ||
            (vInfo.size ?? "").toLowerCase().includes(q) ||
            (i.product?.category ?? "").toLowerCase().includes(q) ||
            (i.product?.brand ?? "").toLowerCase().includes(q) ||
            (i.product?.seller ?? "").toLowerCase().includes(q)
          );
        });
      const matchesStatus = statusFilter === "ALL" || o.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [orders, search, statusFilter]);

  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  async function updateStatus(orderId: string, status: AdminOrder["status"]) {
    const previous = orders;
    let cancelReason: string | undefined = undefined;

    if (status === "CANCELLED") {
      const input = window.prompt("Reason for cancellation (optional):", "Cancelled by admin");
      if (input === null) return; // User pressed cancel in prompt
      cancelReason = input.trim() || "Cancelled by admin";
    }

    setOrders((items) =>
      items.map((o) =>
        o.id === orderId
          ? {
              ...o,
              status,
              ...(status === "CANCELLED"
                ? {
                    cancelReason: cancelReason || o.cancelReason || "Cancelled by admin",
                    cancelledAt: new Date(),
                    cancelledBy: "ADMIN",
                  }
                : {}),
            }
          : o
      )
    );

    try {
      const res = await fetch(`/api/admin/orders/${orderId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status, cancelReason }),
      });
      if (!res.ok) throw new Error("Unable to update order status.");
      toast.success("Order status updated");
    } catch (error) {
      setOrders(previous);
      toast.error(error instanceof Error ? error.message : "Unable to update order status.");
    }
  }

  if (orders.length === 0) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-10 text-center shadow-sm dark:border-white/10 dark:bg-slate-900">
        <p className="font-bold">No orders yet</p>
        <p className="mt-1 text-sm text-slate-500">Orders placed by customers will show up here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900">
          <PackageCheck className="h-6 w-6 text-amazon-teal" />
          <p className="mt-3 text-xs font-black uppercase text-slate-500">Total orders</p>
          <p className="mt-1 text-3xl font-black tracking-normal">{orders.length}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900">
          <Truck className="h-6 w-6 text-amazon-orange" />
          <p className="mt-3 text-xs font-black uppercase text-slate-500">Shipping queue</p>
          <p className="mt-1 text-3xl font-black tracking-normal">
            {orders.filter((o) => ["PROCESSING", "PAID", "PACKED"].includes(o.status)).length}
          </p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900">
          <p className="text-xs font-black uppercase text-slate-500">Revenue</p>
          <p className="mt-3 text-3xl font-black tracking-normal">
            {formatPrice(orders.reduce((sum, o) => sum + o.total, 0))}
          </p>
        </div>
      </div>

      {/* Search + filter */}
      <div className="flex flex-wrap gap-3">
        <div className="relative min-w-48 flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search by order ID, customer, phone, product title, or type…"
            className="h-10 w-full rounded-md border border-slate-200 bg-white pl-9 pr-3 text-sm outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-900"
          />
          {search && (
            <button
              type="button"
              onClick={() => {
                setSearch("");
                setPage(1);
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as typeof statusFilter);
            setPage(1);
          }}
          className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-900"
        >
          <option value="ALL">All statuses</option>
          {ALL_STATUSES.map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
        <p className="flex items-center text-sm text-slate-500">
          {filtered.length} result{filtered.length !== 1 ? "s" : ""}
        </p>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[920px] text-left text-sm">
            <thead className="bg-slate-100 text-xs uppercase text-slate-500 dark:bg-white/10">
              <tr>
                <th className="w-8 px-4 py-3" />
                <th className="px-4 py-3">Order</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Total</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Update</th>
                <th className="px-4 py-3 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-white/10">
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                    No orders found
                  </td>
                </tr>
              ) : (
                paginated.flatMap((order) => {
                  const isExpanded = expandedId === order.id;
                  return [
                    <tr key={order.id} className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
                      <td className="px-4 py-3 align-top">
                        <button
                          type="button"
                          onClick={() => setExpandedId(isExpanded ? null : order.id)}
                          className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-white/10"
                          title={isExpanded ? "Collapse" : "Show details"}
                        >
                          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </button>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <p className="font-mono text-xs font-black text-slate-950 dark:text-white">
                          #{order.id.slice(-8).toUpperCase()}
                        </p>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          {new Date(order.createdAt).toLocaleDateString()}
                        </p>
                        <span className="inline-block mt-1 rounded bg-slate-100 dark:bg-white/10 px-1.5 py-0.2 text-[10px] font-mono text-slate-500 truncate max-w-[110px]" title={order.id}>
                          {order.id}
                        </span>
                        <div className="mt-1">
                          <span className="inline-flex items-center rounded-md bg-[#073B70]/10 px-1.5 py-0.5 text-[10px] font-bold text-[#073B70] dark:bg-[#087DB5]/20 dark:text-sky-300">
                            {order.items.reduce((sum, item) => sum + item.quantity, 0)} item{order.items.reduce((sum, item) => sum + item.quantity, 0) !== 1 ? "s" : ""}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <p className="font-bold text-slate-900 dark:text-white">
                          {order.user?.name ?? order.shippingFullName}
                        </p>
                        <p className="text-xs text-slate-500">{order.user?.email ?? "—"}</p>
                        <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mt-0.5 flex items-center gap-1">
                          <span>📞</span> {order.shippingPhone}
                        </p>
                        <p className="text-xs text-slate-400 mt-0.5">
                          {[order.shippingCity, order.shippingState].filter(Boolean).join(", ") || "Addis Ababa"}
                        </p>
                      </td>
                      <td className="px-4 py-3 align-top font-bold text-slate-900 dark:text-white">
                        {formatPrice(order.total)}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <Badge tone={statusTone(order.status)}>{order.status}</Badge>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <select
                          value={order.status}
                          onChange={(e) => updateStatus(order.id, e.target.value as AdminOrder["status"])}
                          className="h-9 rounded-md border border-slate-200 bg-transparent px-2 text-xs font-semibold outline-none focus:border-amazon-orange dark:border-white/10"
                        >
                          {ALL_STATUSES.map((s) => (
                            <option key={s}>{s}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-3 align-top text-center whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => setDetailModalOrder(order)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-[#073B70]/25 bg-[#073B70]/10 px-3 py-1.5 text-xs font-bold text-[#073B70] hover:bg-[#073B70] hover:text-white transition-all shadow-2xs dark:border-[#087DB5]/40 dark:bg-[#087DB5]/20 dark:text-sky-300 dark:hover:bg-[#087DB5] dark:hover:text-white active:scale-95"
                          title="See full order and product details"
                        >
                          <Eye className="h-3.5 w-3.5" />
                          <span>See Detail</span>
                        </button>
                      </td>
                    </tr>,

                    isExpanded && (
                      <tr key={`${order.id}-detail`} className="bg-slate-50 dark:bg-white/[0.02]">
                        <td colSpan={7} className="px-6 py-5">
                          {/* Cancellation Details Banner */}
                          {(order.status === "CANCELLED" || order.cancelReason) && (
                            <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50/90 p-3.5 text-sm dark:border-rose-900/40 dark:bg-rose-950/30">
                              <div className="flex items-center justify-between gap-2 flex-wrap">
                                <div className="flex items-center gap-2">
                                  <XCircle className="h-4 w-4 text-rose-600 dark:text-rose-400 shrink-0" />
                                  <span className="font-bold text-rose-900 dark:text-rose-200">
                                    Order Cancellation Information
                                  </span>
                                </div>
                                {order.cancelledAt && (
                                  <span className="text-xs text-rose-600 dark:text-rose-400 font-medium">
                                    Cancelled on {new Date(order.cancelledAt).toLocaleString()}{" "}
                                    {order.cancelledBy ? `by ${order.cancelledBy.toLowerCase()}` : ""}
                                  </span>
                                )}
                              </div>
                              <p className="mt-2 text-xs sm:text-sm text-rose-900 dark:text-rose-200">
                                <strong className="font-semibold text-rose-950 dark:text-rose-100">
                                  Cancellation Reason:
                                </strong>{" "}
                                <span className="bg-white/70 dark:bg-black/30 px-2 py-0.5 rounded-md border border-rose-200/60 dark:border-rose-900/30 font-medium">
                                  {order.cancelReason || "No specific reason provided"}
                                </span>
                              </p>
                              {order.paymentStatus === "REFUNDED" && (
                                <p className="mt-2 text-xs text-emerald-700 dark:text-emerald-300 font-semibold flex items-center gap-1.5">
                                  <span>✓</span> Payment was refunded to the customer.
                                </p>
                              )}
                            </div>
                          )}

                          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                            {/* Shipping address */}
                            <div>
                              <p className="mb-2 text-xs font-black uppercase tracking-wider text-slate-400">
                                Shipping destination
                              </p>
                              <p className="font-bold text-slate-900 dark:text-white">{order.shippingFullName}</p>
                              <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-300">{order.shippingLine1}</p>
                              {order.shippingLine2 && (
                                <p className="text-sm text-slate-600 dark:text-slate-300">{order.shippingLine2}</p>
                              )}
                              {[order.shippingCity, order.shippingState, order.shippingPincode]
                                .filter(Boolean)
                                .join(", ") && (
                                <p className="text-sm text-slate-600 dark:text-slate-300">
                                  {[order.shippingCity, order.shippingState, order.shippingPincode]
                                    .filter(Boolean)
                                    .join(", ")}
                                </p>
                              )}
                              <p className="mt-1.5 flex items-center gap-1.5 text-sm font-bold text-slate-700 dark:text-slate-200">
                                <span>📞</span>
                                {order.shippingPhone}
                              </p>
                            </div>

                            {/* Financials */}
                            <div>
                              <p className="mb-2 text-xs font-black uppercase tracking-wider text-slate-400">
                                Financial breakdown
                              </p>
                              <div className="space-y-1 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-slate-500">Subtotal</span>
                                  <span>{formatPrice(order.subtotal)}</span>
                                </div>
                                {order.discount > 0 && (
                                  <div className="flex justify-between text-amazon-green">
                                    <span>Discount</span>
                                    <span>-{formatPrice(order.discount)}</span>
                                  </div>
                                )}
                                <div className="flex justify-between">
                                  <span className="text-slate-500">Shipping</span>
                                  <span>{order.shipping === 0 ? "FREE" : formatPrice(order.shipping)}</span>
                                </div>
                                {order.tax > 0 && (
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">Tax</span>
                                    <span>{formatPrice(order.tax)}</span>
                                  </div>
                                )}
                                <div className="flex justify-between border-t pt-1.5 font-bold dark:border-white/10">
                                  <span>Total</span>
                                  <span>{formatPrice(order.total)}</span>
                                </div>
                              </div>
                              <p className="mt-2 text-xs text-slate-400">
                                Payment:{" "}
                                <span className="font-bold text-slate-600 dark:text-slate-300">
                                  {order.paymentStatus}
                                </span>
                                {" · "}
                                {order.paymentProvider}
                              </p>
                            </div>

                            {/* Detailed Products & Type Breakdown */}
                            <div>
                              <div className="mb-2 flex items-center justify-between">
                                <span className="text-xs font-black uppercase tracking-wider text-slate-400">
                                  Ordered Products ({order.items.length})
                                </span>
                                <button
                                  type="button"
                                  onClick={() => setDetailModalOrder(order)}
                                  className="text-[11px] font-bold text-[#087DB5] hover:underline flex items-center gap-1"
                                >
                                  <Eye className="h-3.5 w-3.5" /> Open Detail Modal
                                </button>
                              </div>
                              <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                                {order.items.map((item) => {
                                  const badge = getCategoryBadge(item.product?.category);
                                  return (
                                    <div
                                      key={item.id}
                                      className="rounded-xl border border-slate-200 bg-white p-2.5 shadow-2xs dark:border-white/10 dark:bg-slate-800"
                                    >
                                      <div className="flex items-start gap-2.5">
                                        {item.image ? (
                                          <button
                                            type="button"
                                            onClick={() => setEnlargedImage({ url: item.image, title: item.title })}
                                            className="group relative h-12 w-12 shrink-0 rounded-lg object-contain bg-white border border-slate-200 dark:border-white/10 p-0.5 hover:border-[#087DB5] transition-all overflow-hidden cursor-zoom-in"
                                            title="Click to view large image"
                                          >
                                            <img
                                              src={item.image}
                                              alt={item.title}
                                              className="h-full w-full object-contain group-hover:scale-105 transition-transform"
                                            />
                                            <div className="absolute inset-0 bg-black/35 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity text-white">
                                              <Maximize2 className="h-3.5 w-3.5" />
                                            </div>
                                          </button>
                                        ) : (
                                          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-400 dark:bg-slate-700">
                                            <Package className="h-4 w-4" />
                                          </div>
                                        )}
                                        <div className="min-w-0 flex-1">
                                          <div className="flex items-start justify-between gap-1.5">
                                            <p className="text-xs font-bold leading-snug text-slate-900 dark:text-white line-clamp-1">
                                              {item.title}
                                            </p>
                                            <span className="shrink-0 text-xs font-bold text-slate-900 dark:text-white">
                                              {formatPrice(item.price * item.quantity)}
                                            </span>
                                          </div>

                                          <div className="mt-1 flex flex-wrap items-center gap-1">
                                            <span
                                              className={`inline-flex items-center rounded border px-1.5 py-0.2 text-[9.5px] font-bold ${badge.color}`}
                                            >
                                              {badge.label}
                                            </span>
                                            {item.product?.brand && (
                                              <span className="inline-flex items-center rounded bg-slate-100 px-1.5 py-0.2 text-[9.5px] font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                                                Brand: {item.product.brand}
                                              </span>
                                            )}
                                            <span className="text-[10px] font-bold text-slate-600 dark:text-slate-400">
                                              Qty: {item.quantity} × {formatPrice(item.price)}
                                            </span>
                                          </div>

                                          <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px]">
                                            <ItemVariantBadge item={item} size="md" showDefaultFallback={true} />
                                            {item.sku && (
                                              <span className="text-[10px] font-mono text-slate-400">
                                                SKU: {item.sku}
                                              </span>
                                            )}
                                            {(item.product?.seller || item.vendorId) && (
                                              <span className="text-[10.5px] text-slate-500">
                                                Seller: <strong className="text-slate-700 dark:text-slate-300">{item.product?.seller || "Vendor"}</strong>
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ),
                  ].filter(Boolean);
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Order & Products Detail Modal */}
      {activeModalOrder && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs animate-in fade-in duration-200"
          onClick={() => setDetailModalOrder(null)}
        >
          <div
            className="relative flex max-h-[92vh] w-full max-w-4xl flex-col rounded-2xl bg-white shadow-2xl dark:border dark:border-white/10 dark:bg-slate-900"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 dark:border-white/10">
              <div className="flex flex-wrap items-center gap-3">
                <span className="rounded-lg bg-[#073B70]/10 px-2.5 py-1 font-mono text-sm font-black text-[#073B70] dark:bg-[#087DB5]/20 dark:text-sky-300">
                  #{activeModalOrder.id.slice(-8).toUpperCase()}
                </span>
                <Badge tone={statusTone(activeModalOrder.status)}>{activeModalOrder.status}</Badge>
                <span className="text-xs text-slate-500">
                  Ordered on {new Date(activeModalOrder.createdAt).toLocaleString()}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setDetailModalOrder(null)}
                className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-white/10 dark:hover:text-white transition-colors"
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Scrollable Body */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
              {/* Ordered Products Section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-black uppercase tracking-wider text-slate-700 dark:text-slate-300 flex items-center gap-2">
                    <Package className="h-4 w-4 text-[#087DB5]" />
                    Ordered Products ({activeModalOrder.items.length})
                  </h3>
                  <span className="text-xs text-slate-400">
                    Click any image to view in high resolution
                  </span>
                </div>

                <div className="space-y-3.5">
                  {activeModalOrder.items.map((item) => {
                    const badge = getCategoryBadge(item.product?.category);
                    const vInfo = resolveItemVariant(item);

                    return (
                      <div
                        key={item.id}
                        className="rounded-xl border border-slate-200 bg-slate-50/70 p-4 transition-colors hover:border-[#087DB5]/40 dark:border-white/10 dark:bg-slate-800/50"
                      >
                        <div className="flex flex-col sm:flex-row items-start gap-4">
                          {/* Product Image */}
                          <div className="relative shrink-0 mx-auto sm:mx-0">
                            {item.image ? (
                              <button
                                type="button"
                                onClick={() => setEnlargedImage({ url: item.image, title: item.title })}
                                className="group relative block h-28 w-28 sm:h-32 sm:w-32 rounded-xl bg-white border border-slate-200 dark:border-white/10 p-1.5 shadow-sm hover:border-[#087DB5] transition-all overflow-hidden cursor-zoom-in"
                                title="Click to view large image"
                              >
                                <img
                                  src={item.image}
                                  alt={item.title}
                                  className="h-full w-full object-contain group-hover:scale-105 transition-transform"
                                />
                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex flex-col items-center justify-center transition-opacity text-white text-[10px] font-bold gap-1">
                                  <Maximize2 className="h-4 w-4" />
                                  <span>Enlarge Image</span>
                                </div>
                              </button>
                            ) : (
                              <div className="flex h-28 w-28 sm:h-32 sm:w-32 items-center justify-center rounded-xl bg-slate-100 text-slate-400 dark:bg-slate-700">
                                <Package className="h-8 w-8" />
                              </div>
                            )}
                          </div>

                          {/* Product Details */}
                          <div className="min-w-0 flex-1 space-y-2 w-full">
                            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                              <div>
                                <h4 className="text-sm sm:text-base font-bold text-slate-900 dark:text-white leading-snug">
                                  {item.title}
                                </h4>
                                {item.product?.slug && (
                                  <a
                                    href={`/products/${item.product.slug}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1 text-xs font-semibold text-[#087DB5] hover:underline mt-0.5"
                                  >
                                    <span>View Product in Store</span>
                                    <ExternalLink className="h-3 w-3" />
                                  </a>
                                )}
                              </div>
                              <div className="sm:text-right shrink-0">
                                <span className="text-base font-black text-slate-900 dark:text-white">
                                  {formatPrice(item.price * item.quantity)}
                                </span>
                                <p className="text-xs text-slate-500">
                                  {formatPrice(item.price)} × {item.quantity}
                                </p>
                              </div>
                            </div>

                            {/* Badges: Category, Brand, Color Swatch, Size */}
                            <div className="flex flex-wrap items-center gap-2 pt-1">
                              <span
                                className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-bold ${badge.color}`}
                              >
                                {badge.label}
                              </span>

                              {item.product?.brand && (
                                <span className="inline-flex items-center rounded-md bg-slate-200/80 px-2 py-0.5 text-xs font-semibold text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                                  Brand: {item.product.brand}
                                </span>
                              )}

                              {/* Highlighted Color with Circle Swatch */}
                              {vInfo.color ? (
                                <div className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-0.5 text-xs font-bold text-slate-900 shadow-2xs dark:border-white/10 dark:bg-slate-700 dark:text-white">
                                  <span
                                    className="h-3.5 w-3.5 rounded-full border border-black/20 shadow-xs shrink-0"
                                    style={{ backgroundColor: vInfo.colorCode || "#64748B" }}
                                  />
                                  <span>
                                    Color: <strong className="font-extrabold">{vInfo.color}</strong>
                                  </span>
                                </div>
                              ) : (
                                <span className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-xs text-slate-500 dark:bg-slate-700 dark:text-slate-400">
                                  Standard Color Edition
                                </span>
                              )}

                              {/* Size Badge */}
                              {vInfo.size && (
                                <span className="inline-flex items-center rounded-md border border-slate-300 bg-slate-100 px-2.5 py-0.5 text-xs font-bold text-slate-800 dark:border-white/10 dark:bg-slate-700 dark:text-slate-100">
                                  Size: {vInfo.size}
                                </span>
                              )}

                              {item.sku && (
                                <span className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 font-mono text-[11px] text-slate-500 dark:bg-slate-700 dark:text-slate-400">
                                  SKU: {item.sku}
                                </span>
                              )}
                            </div>

                            {/* Seller Attribution & Variant Title */}
                            <div className="flex flex-wrap items-center gap-4 text-xs text-slate-600 dark:text-slate-400 pt-1 border-t border-slate-200/60 dark:border-white/5">
                              {(item.product?.seller || item.vendorId) && (
                                <span>
                                  Sold by:{" "}
                                  <strong className="text-slate-900 dark:text-white font-semibold">
                                    {item.product?.seller || "Registered Vendor"}
                                  </strong>
                                </span>
                              )}
                              {item.variantTitle && (
                                <span>
                                  Variant Tag:{" "}
                                  <strong className="text-slate-700 dark:text-slate-300 font-medium">
                                    {item.variantTitle}
                                  </strong>
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Customer, Shipping, Financial Details Grid */}
              <div className="grid gap-4 sm:grid-cols-2">
                {/* Shipping Details */}
                <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-800">
                  <h4 className="text-xs font-black uppercase tracking-wider text-slate-400 flex items-center gap-1.5 mb-2.5">
                    <MapPin className="h-3.5 w-3.5 text-[#087DB5]" />
                    Delivery Destination & Customer
                  </h4>
                  <p className="font-bold text-sm text-slate-900 dark:text-white">
                    {activeModalOrder.user?.name || activeModalOrder.shippingFullName}
                  </p>
                  {activeModalOrder.user?.email && (
                    <p className="text-xs text-slate-500">{activeModalOrder.user.email}</p>
                  )}
                  <p className="mt-1 text-xs text-slate-700 dark:text-slate-300">
                    {activeModalOrder.shippingLine1}
                    {activeModalOrder.shippingLine2 ? `, ${activeModalOrder.shippingLine2}` : ""}
                  </p>
                  <p className="text-xs text-slate-700 dark:text-slate-300">
                    {[activeModalOrder.shippingCity, activeModalOrder.shippingState, activeModalOrder.shippingPincode]
                      .filter(Boolean)
                      .join(", ") || "Addis Ababa"}
                  </p>
                  <p className="mt-2 text-xs font-bold text-slate-800 dark:text-slate-200 flex items-center gap-1.5">
                    <Phone className="h-3.5 w-3.5 text-emerald-600" />
                    <a href={`tel:${activeModalOrder.shippingPhone}`} className="hover:underline">
                      {activeModalOrder.shippingPhone}
                    </a>
                  </p>
                </div>

                {/* Financial Breakdown */}
                <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-800">
                  <h4 className="text-xs font-black uppercase tracking-wider text-slate-400 flex items-center gap-1.5 mb-2.5">
                    <CreditCard className="h-3.5 w-3.5 text-[#087DB5]" />
                    Financial Breakdown
                  </h4>
                  <div className="space-y-1.5 text-xs">
                    <div className="flex justify-between text-slate-600 dark:text-slate-300">
                      <span>Subtotal</span>
                      <span className="font-semibold">{formatPrice(activeModalOrder.subtotal)}</span>
                    </div>
                    {activeModalOrder.discount > 0 && (
                      <div className="flex justify-between text-emerald-600">
                        <span>Discount</span>
                        <span>-{formatPrice(activeModalOrder.discount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-slate-600 dark:text-slate-300">
                      <span>Shipping</span>
                      <span className="font-semibold">
                        {activeModalOrder.shipping === 0 ? "FREE" : formatPrice(activeModalOrder.shipping)}
                      </span>
                    </div>
                    {activeModalOrder.tax > 0 && (
                      <div className="flex justify-between text-slate-600 dark:text-slate-300">
                        <span>Tax</span>
                        <span>{formatPrice(activeModalOrder.tax)}</span>
                      </div>
                    )}
                    <div className="flex justify-between border-t border-slate-200 pt-2 text-sm font-black text-slate-900 dark:border-white/10 dark:text-white">
                      <span>Total Amount</span>
                      <span>{formatPrice(activeModalOrder.total)}</span>
                    </div>
                  </div>
                  <p className="mt-3 text-xs text-slate-500 flex items-center gap-1">
                    Payment Status:{" "}
                    <span className="font-bold text-slate-800 dark:text-slate-200">
                      {activeModalOrder.paymentStatus}
                    </span>
                    {" · "}
                    <span>{activeModalOrder.paymentProvider}</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-6 py-3.5 bg-slate-50 rounded-b-2xl dark:border-white/10 dark:bg-slate-800/80">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-slate-600 dark:text-slate-300">
                  Update Status:
                </span>
                <select
                  value={activeModalOrder.status}
                  onChange={(e) => updateStatus(activeModalOrder.id, e.target.value as AdminOrder["status"])}
                  className="h-8 rounded-lg border border-slate-300 bg-white px-2.5 text-xs font-bold text-slate-800 outline-none focus:border-[#087DB5] dark:border-white/10 dark:bg-slate-700 dark:text-white"
                >
                  {ALL_STATUSES.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                onClick={() => setDetailModalOrder(null)}
                className="rounded-lg bg-slate-200 px-4 py-2 text-xs font-bold text-slate-800 hover:bg-slate-300 dark:bg-white/10 dark:text-slate-200 dark:hover:bg-white/20 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enlarged Image Lightbox */}
      {enlargedImage && (
        <div
          className="fixed inset-0 z-60 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm animate-in fade-in duration-150 cursor-pointer"
          onClick={() => setEnlargedImage(null)}
        >
          <div
            className="relative max-h-[90vh] max-w-[90vw] overflow-hidden rounded-2xl bg-white p-2 shadow-2xl dark:bg-slate-900 cursor-default"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setEnlargedImage(null)}
              className="absolute top-4 right-4 z-10 rounded-full bg-black/60 p-2 text-white hover:bg-black transition-colors"
              title="Close image"
            >
              <X className="h-5 w-5" />
            </button>
            <img
              src={enlargedImage.url}
              alt={enlargedImage.title}
              className="max-h-[80vh] max-w-full rounded-xl object-contain mx-auto"
            />
            <p className="mt-2 text-center text-xs font-bold text-slate-700 dark:text-slate-300">
              {enlargedImage.title}
            </p>
          </div>
        </div>
      )}

      {/* Pagination */}
      <AdminPagination
        currentPage={page}
        totalItems={filtered.length}
        perPage={perPage}
        onPageChange={setPage}
        onPerPageChange={(newPerPage) => {
          setPerPage(newPerPage);
          setPage(1);
        }}
        itemLabel="orders"
      />
    </div>
  );
}
